/**
 * Billing Agent.
 *
 * Thin wrapper around {@link APIAgent} for the Stripe billing endpoints,
 * following the same shape as `subscriptionAdminAgent.js` — a low-level
 * `billingAgent` object whose methods call `APIAgent.request(...)`, plus the
 * React hooks layer that consumes it (see `hooks/useSubscription.js` and
 * `hooks/useStartCheckout.js`).
 *
 * Error handling mirrors `APIAgent.request`: failures are thrown as `Error`
 * instances carrying `.status` / `.statusCode`. Callers `try/catch` and surface
 * `err.message` (or branch on `err.status`) to the UI.
 *
 * PR2 scope: `startCheckoutSession` + `getSubscription` are fully wired against
 * the pinned API contract. PR4 adds the real `openCustomerPortal` (Stripe
 * Customer Portal session) + `getInvoices` (cursor-paginated invoice list).
 * PR5 adds `createOneTimeCharge` (Full Service flat software fee at listing
 * publish — server-initiated PaymentIntent confirmed in-modal via Stripe.js).
 */

import APIAgent from './apiAgent';

const BASE = '/api/billing';

export const billingAgent = {
  /**
   * Create a Stripe Checkout session for a subscription plan.
   *
   * Contract — POST /api/billing/checkout-session
   *   Request body: { plan_code, billing_interval }
   *   Response 200: { checkout_url }
   *   401 not authed · 503 billing disabled · 422 invalid plan/interval ·
   *   502 payment-provider error.
   *
   * @param {string} planCode - Plan code of a subscription plan.
   * @param {string} [billingInterval='monthly'] - Billing interval.
   * @returns {Promise<{ checkout_url: string }>} The hosted Checkout URL.
   */
  startCheckoutSession(planCode, billingInterval = 'monthly') {
    return APIAgent.request(`${BASE}/checkout-session`, 'POST', {
      plan_code: planCode,
      billing_interval: billingInterval,
    });
  },

  /**
   * Fetch the current user's subscription state (local mirror — no Stripe call).
   *
   * Contract — GET /api/billing/subscription
   *   Response 200: {
   *     plan_code: string,
   *     status: string|null,
   *     current_period_end: string|null,
   *     cancel_at_period_end: boolean,
   *     trial_end: string|null
   *   }
   *   Free users get { plan_code: 'explorer', status: null, ... }.
   *   401 not authed.
   *
   * @returns {Promise<object>} Subscription state object.
   */
  getSubscription() {
    return APIAgent.request(`${BASE}/subscription`, 'GET');
  },

  /**
   * Open a Stripe Customer Portal session (manage payment method / cancel).
   *
   * Contract — POST /api/billing/portal-session
   *   Request body: { return_url }  (optional; backend defaults to /billing)
   *   Response 200: { portal_url }
   *   401 not authed ·
   *   503 billing disabled ·
   *   400 { detail: "No billing account found" } if the user never checked out ·
   *   502 on Stripe error.
   *
   * The caller (`useCustomerPortal`) catches and branches on `err.status` to map
   * 400 → "choose a plan first" and 503 → "billing not available yet".
   *
   * @param {object} [opts]
   * @param {string} [opts.returnUrl] - Where Stripe sends the user back to.
   * @returns {Promise<{ portal_url: string }>} The hosted Customer Portal URL.
   */
  openCustomerPortal({ returnUrl } = {}) {
    return APIAgent.request(`${BASE}/portal-session`, 'POST', {
      return_url: returnUrl,
    });
  },

  /**
   * Fetch a page of the current user's invoices (cursor pagination).
   *
   * Contract — GET /api/billing/invoices?limit=<n>&cursor=<id>
   *   Response 200: {
   *     invoices: [{
   *       id, stripe_invoice_id, amount_cents, currency, status,
   *       hosted_invoice_url, period_start, period_end, created_at, paid_at
   *     }],
   *     next_cursor: <id|null>
   *   }
   *   Empty `{ invoices: [], next_cursor: null }` when none / billing disabled.
   *   401 not authed.
   *
   * @param {object} [opts]
   * @param {number} [opts.limit=10] - Page size.
   * @param {string|number|null} [opts.cursor=null] - The `next_cursor` from the
   *   previous page; omitted for the first page.
   * @returns {Promise<{ invoices: object[], next_cursor: (string|number|null) }>}
   */
  getInvoices({ limit = 10, cursor = null } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor != null && cursor !== '') params.set('cursor', String(cursor));
    return APIAgent.request(`${BASE}/invoices?${params}`, 'GET');
  },

  /**
   * Create (or reuse) the Full Service one-time charge for a listing publish.
   *
   * The flat software fee ($799, or $1,499 at a $1M+ list price) is
   * **seller-paid** and charged **at publish time**
   * — when the seller takes a `draft` `House` live. The backend creates an
   * idempotent PaymentIntent against the seller's Stripe customer, scoped to this
   * listing, so a retry never double-charges. The returned `client_secret` is
   * confirmed in-modal via Stripe.js (`OneTimeChargeModal`); on webhook success
   * the backend transitions the `House` `draft → active`.
   *
   * Contract — POST /api/billing/one-time-charge
   *   Request body: { house_id }
   *   Response 200: { client_secret, amount_cents, publishable_key }
   *   401 not authed ·
   *   403 { detail } — caller is not the seller / listing owner ·
   *   404 { detail } — no such house ·
   *   409 { detail: "Listing already paid" } — already paid (caller treats this
   *       as success: the listing can publish / is published) ·
   *   503 billing disabled ·
   *   502 Stripe error.
   *
   * Errors are thrown by `APIAgent.request` as `Error` instances carrying
   * `.status` / `.statusCode`; `OneTimeChargeModal` branches on `err.status`
   * (409 → already-paid success path; 403/404/503/502 → inline error copy).
   *
   * @param {number|string} houseId - The `House` being published.
   * @returns {Promise<{ client_secret: string, amount_cents: number, publishable_key: string }>}
   */
  createOneTimeCharge(houseId) {
    return APIAgent.request(`${BASE}/one-time-charge`, 'POST', {
      house_id: houseId,
    });
  },

  /**
   * Whether billing is switched on for this environment.
   *
   * Contract — GET /api/billing/config
   *   Response 200: { billing_enabled: boolean }
   *   Unauthenticated-safe and never 503. `skipAuthRedirect` so a signed-out
   *   caller is never bounced to `/` by this probe.
   *
   * @returns {Promise<{ billing_enabled: boolean }>}
   */
  getConfig() {
    return APIAgent.request(`${BASE}/config`, 'GET', null, {}, { skipAuthRedirect: true });
  },

  /**
   * The Full Service fee position for one listing (drives the wizard's Listing
   * Fee step).
   *
   * Contract — GET /api/billing/listings/{house_id}/fee  (seller only)
   *   Response 200: { billing_enabled, tier_amount_cents, paid_cents, amount_due_cents }
   *   Billing off → { billing_enabled: false, ..., amount_due_cents: 0 }.
   *
   * @param {number|string} houseId
   * @returns {Promise<{ billing_enabled: boolean, tier_amount_cents: number, paid_cents: number, amount_due_cents: number }>}
   */
  getListingFee(houseId) {
    return APIAgent.request(`${BASE}/listings/${encodeURIComponent(houseId)}/fee`, 'GET');
  },
};

export default billingAgent;
